'use client';

import { c as _c } from "react/compiler-runtime";
import { LayoutContainer, Section } from '@petsmart-ui/sparky';
import cn from 'classnames';
import dynamic from 'next/dynamic';
import { usePathname } from 'next/navigation';
import type { FC } from 'react';
import { useSitePreviewData } from '@libs/common-next';
import type { Offer } from '@libs/common-types';
import type { CardUpVariantType } from '../../types';
import { getPlpModuleSectionHeading, getTrimmedDisplayTitle } from '../../utils/plpModuleSectionHeading';
import { ColoredTextLink } from '../ColoredTextLink';
import { trackCardUpLinkActionClick } from './analytics';
import CardUpVariant from './CardUpVariant';
import { OfferPattern } from './offer-pattern';
import type { Card, CardUpBrandProps, CardUpOfferProps, CardUpProps } from './types';
import { prepareEligibleOffers } from './utils';
import './CardInfo.scss';
import './CardUp.scss';
import './CardUpArticle.scss';
const CardUpArticleVariant = dynamic(() => import('./CardUpArticleVariant'), {
  ssr: false
});
const CardUpBrandVariant = dynamic(() => import('./CardUpBrandVariant'), {
  ssr: false
});
const CardUpOfferVariant = dynamic(() => import('./card-up-offer-variant'));
const ComponentCardUp: Record<CardUpVariantType, FC<CardUpProps>> = {
  '2up': props => <CardUpVariant sliceMax={2} {...props} />,
  '2up Brand': props => <CardUpBrandVariant sliceMax={2} {...props as unknown as CardUpBrandProps} />,
  '3up': props => <CardUpVariant sliceMax={3} {...props} />,
  '4up': props => <CardUpVariant sliceMax={4} {...props} />,
  '4Stack': props => <CardUpVariant sliceMax={4} {...props} />,
  '3up Article': props => <CardUpArticleVariant articlesPerView={3} {...props} />,
  Info: props => <CardUpVariant sliceMax={4} {...props} />,
  Offer: props => <CardUpOfferVariant {...props as unknown as CardUpOfferProps} />
};
const validateCardTypes = (cards: Card[]): boolean => {
  const articleModel = cards.some(card => card.previewDescription);
  const articleModelsAll = cards.every(card => card.previewDescription);
  return articleModel && !articleModelsAll;
};
const getEligibleCards = (variant: CardUpVariantType, cards: Card[], previewDate?: string): Card[] | Offer[] => {
  if (variant === 'Offer') {
    return prepareEligibleOffers(cards ?? [], previewDate ? new Date(previewDate) : new Date());
  }
  return cards;
};
export default function CardUp(props) {
  const $ = _c(4);
  if (props.variant === "Offer") {
    let t0;
    if ($[0] !== props) {
      t0 = <OfferPattern><CardUpSection {...props} /></OfferPattern>;
      $[0] = props;
      $[1] = t0;
    } else {
      t0 = $[1];
    }
    return t0;
  }
  let t0;
  if ($[2] !== props) {
    t0 = <CardUpSection {...props} />;
    $[2] = props;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  return t0;
}
function CardUpSection(t0) {
  const $ = _c(54);
  const {
    header,
    description,
    linkAction,
    linkActionColor,
    variant,
    cards,
    backgroundColor,
    isH1
  } = t0;
  const pathname = usePathname();
  const previewDate = useSitePreviewData()?.previewDate;
  let t1;
  if ($[0] !== cards || $[1] !== previewDate || $[2] !== variant) {
    t1 = getEligibleCards(variant, cards, previewDate);
    $[0] = cards;
    $[1] = previewDate;
    $[2] = variant;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const cardsToRender = t1;
  if (!cardsToRender?.length) {
    return null;
  }
  const Component = ComponentCardUp[variant];
  if (!Component) {
    return null;
  }
  if (variant === "3up Article" && validateCardTypes(cards)) {
    return null;
  }
  let t2;
  if ($[4] !== linkAction) {
    t2 = linkAction || {};
    $[4] = linkAction;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const {
    link,
    actionText
  } = t2;
  let t3;
  if ($[6] !== header) {
    t3 = getTrimmedDisplayTitle(header);
    $[6] = header;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const displayHeader = t3;
  let t4;
  if ($[8] !== header || $[9] !== isH1) {
    t4 = getPlpModuleSectionHeading(header, isH1);
    $[8] = header;
    $[9] = isH1;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const {
    title: sectionTitle,
    headingTagName
  } = t4;
  let t5;
  if ($[11] !== actionText || $[12] !== cardsToRender || $[13] !== displayHeader || $[14] !== header || $[15] !== link || $[16] !== linkAction || $[17] !== linkActionColor || $[18] !== pathname || $[19] !== variant) {
    t5 = displayHeader && link && actionText ? <ColoredTextLink data-testid="link-action" onClick={() => trackCardUpLinkActionClick({
      header,
      linkAction,
      offerCount: cardsToRender.length,
      pathname: pathname ?? "",
      variant
    })} href={link?.webUrl} color={linkActionColor}>{actionText}</ColoredTextLink> : null;
    $[11] = actionText;
    $[12] = cardsToRender;
    $[13] = displayHeader;
    $[14] = header;
    $[15] = link;
    $[16] = linkAction;
    $[17] = linkActionColor;
    $[18] = pathname;
    $[19] = variant;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const titleAfter = t5;
  let isRestyled;
  let t6;
  let variantName;
  if ($[21] !== displayHeader || $[22] !== variant) {
    variantName = variant.toLowerCase().replaceAll(" ", "-");
    const noHeaderClass = !displayHeader ? `${variantName}--no-header` : "";
    let t7;
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = ["2up", "3up", "3up Article", "4up", "Info", "4Stack", "Offer"];
      $[26] = t7;
    } else {
      t7 = $[26];
    }
    const restyledVariants = t7;
    isRestyled = restyledVariants.includes(variant);
    t6 = cn("card-up", variantName, noHeaderClass, isRestyled && "card-up--restyled");
    $[21] = displayHeader;
    $[22] = variant;
    $[23] = isRestyled;
    $[24] = t6;
    $[25] = variantName;
  } else {
    isRestyled = $[23];
    t6 = $[24];
    variantName = $[25];
  }
  const componentClassName = t6;
  const cardHeaderTagName = isH1 || !displayHeader ? "h2" : "h3";
  const t7 = `card-up-container card-up-container--${variantName}`;
  let t8;
  if ($[27] !== backgroundColor) {
    t8 = {
      backgroundColor
    };
    $[27] = backgroundColor;
    $[28] = t8;
  } else {
    t8 = $[28];
  }
  let t9;
  if ($[29] !== isRestyled) {
    t9 = isRestyled && {
      align: "center",
      titleAfterLocation: "below-description"
    };
    $[29] = isRestyled;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  const t10 = cardsToRender as Card[];
  const t11 = pathname ?? "";
  let t12;
  if ($[31] !== Component || $[32] !== cardHeaderTagName || $[33] !== description || $[34] !== header || $[35] !== isH1 || $[36] !== linkAction || $[37] !== t10 || $[38] !== t11 || $[39] !== variant) {
    t12 = <Component header={header} description={description} linkAction={linkAction} cards={t10} variant={variant} pathname={t11} isH1={isH1} cardHeaderTagName={cardHeaderTagName} />;
    $[31] = Component;
    $[32] = cardHeaderTagName;
    $[33] = description;
    $[34] = header;
    $[35] = isH1;
    $[36] = linkAction;
    $[37] = t10;
    $[38] = t11;
    $[39] = variant;
    $[40] = t12;
  } else {
    t12 = $[40];
  }
  let t13;
  if ($[41] !== componentClassName || $[42] !== description || $[43] !== headingTagName || $[44] !== sectionTitle || $[45] !== t12 || $[46] !== t8 || $[47] !== t9 || $[48] !== titleAfter || $[49] !== variantName) {
    t13 = <Section data-testid="card-up" data-variant={variantName} className={componentClassName} title={sectionTitle} description={description} titleAfter={titleAfter} style={t8} headingTagName={headingTagName} {...t9}>{t12}</Section>;
    $[41] = componentClassName;
    $[42] = description;
    $[43] = headingTagName;
    $[44] = sectionTitle;
    $[45] = t12;
    $[46] = t8;
    $[47] = t9;
    $[48] = titleAfter;
    $[49] = variantName;
    $[50] = t13;
  } else {
    t13 = $[50];
  }
  let t14;
  if ($[51] !== t13 || $[52] !== t7) {
    t14 = <LayoutContainer className={t7}>{t13}</LayoutContainer>;
    $[51] = t13;
    $[52] = t7;
    $[53] = t14;
  } else {
    t14 = $[53];
  }
  return t14;
}